import { invoke, omit } from 'lodash';
import { transformValuesToString } from './utils';
import {
  TRANSFORMATION_DATA_TYPE_URL_PARAMS,
  TRANSFORMATION_DATA_TYPE_JSON,
  PORTS,
} from './constants';

// we suppress errors since they are many and of no value to us
export const suppressedStatuses = [
  408,
  '408',
  429,
  '429',
  500,
  '500',
  501,
  '501',
  502,
  '502',
  503,
  '503',
  504,
  '504',
];

export const populateRequestData = (data = {}) => {
  const updatedData = {
    json: {
      ...Pollfish.helper.getRedirectData(),
      ...data,
      survey_session_id: invoke(Pollfish, 'survey.getSurveySessionId'),
    },
    preview: Pollfish.helper.isPreview,
    dontencrypt: true,
    hash: false,
  };

  if (updatedData.json.reward_name) {
    const jsonRewardName = updatedData.json.reward_name;
    const rewardName = decodeURIComponent(jsonRewardName);
    updatedData.json.reward_name =
      rewardName !== jsonRewardName ? rewardName : jsonRewardName;
  }
  if (updatedData.json.reward_conversion_hash) {
    const jsonRewardConversionHash = updatedData.json.reward_conversion_hash;
    const rewardConversionHash = decodeURIComponent(jsonRewardConversionHash);
    updatedData.json.reward_conversion_hash =
      rewardConversionHash !== jsonRewardConversionHash
        ? rewardConversionHash
        : jsonRewardConversionHash;
  }

  const sig = Pollfish.helper.getSignature();
  if (sig) {
    // we don't have to encode this
    updatedData.sig = sig;
  }
  return updatedData;
};

const dataTransformationForGet = (data = {}) => {
  const updatedData = populateRequestData(data);
  const updatedJSON = transformValuesToString(updatedData.json);
  updatedData.json = JSON.stringify(updatedJSON);
  return updatedData;
};

export const getRequestDataAsUrlParams = (data = {}) => {
  const updatedData = populateRequestData(data);
  const updatedJSON = omit(updatedData.json, [
    'lastSurveyId',
    'redirectURL',
    'always_return_content',
  ]);
  updatedJSON.reward_name = encodeURIComponent(updatedJSON.reward_name);
  updatedJSON.reward_conversion_hash = encodeURIComponent(
    updatedJSON.reward_conversion_hash,
  );
  updatedData.json = transformValuesToString(updatedJSON);
  const restParams = omit(updatedData, ['preview']);
  let queryParams = `json=${JSON.stringify(restParams.json)}&dontencrypt=${
    restParams.dontencrypt
  }&hash=${restParams.hash}`;
  if (restParams.sig) {
    queryParams += `&sig=${encodeURIComponent(restParams.sig)}`;
  }
  return queryParams;
};

export const dataTransformationToJsonForPost = (data = {}) => {
  const updatedData = populateRequestData(data);
  const updatedJSON = omit(updatedData.json, [
    'lastSurveyId',
    'redirectURL',
    'always_return_content',
  ]);
  updatedData.json = transformValuesToString(updatedJSON);
  const restParams = omit(updatedData, ['preview']);
  return JSON.stringify(restParams);
};

const request = ({
  url,
  method = 'GET',
  contentType,
  data,
  transformationDataType = TRANSFORMATION_DATA_TYPE_URL_PARAMS,
  transformData = true,
  dataType,
}) => {
  // monitoring tracker. We should keep it only for a short while (famous last words)
  // we also only want to report this issue once per session so that we dont get overcrowded
  if (
    !Pollfish.store.reportedRejectionErrors &&
    url.indexOf('notresponsible') >= 0
  ) {
    const surveyId = data.s_id;
    if (!Pollfish.store.monitoring.rejectedSurveys[surveyId]) {
      Pollfish.store.monitoring.rejectedSurveys[surveyId] = 0;
    }
    Pollfish.store.monitoring.rejectedSurveys[surveyId] += 1;
    // 5 because we are trying to fine-tune false positives
    if (Pollfish.store.monitoring.rejectedSurveys[surveyId] >= 5) {
      Pollfish.store.monitoring.reportedRejectionErrors = true;
      Raven.captureMessage('Multiple Not Responsible events called', {
        extra: {
          url,
          method,
          data,
          redirectData: Pollfish.helper.getRedirectData(),
        },
      });
    }
  }
  const requestMethod = method.toUpperCase();
  let requestData;
  if (transformData && requestMethod === 'GET') {
    requestData = dataTransformationForGet(data);
  } else if (
    transformData &&
    requestMethod === 'POST' &&
    transformationDataType === TRANSFORMATION_DATA_TYPE_JSON
  ) {
    requestData = dataTransformationToJsonForPost(data);
  } else if (transformData && requestMethod === 'POST') {
    requestData = getRequestDataAsUrlParams(data);
  } else {
    requestData = JSON.stringify(data);
  }
  if (Pollfish.helper.isDevelopment()) {
    Pollfish.helper.baseRequestURL = `http://localhost:${PORTS.MOCK}`; // dyson mock api
    // eslint-disable-next-line
    console.log(
      `Will make a ${requestMethod.toUpperCase()} request to ${Pollfish.helper.getBaseRequestURL()}${url} with data ${requestData}`,
    );
  }

  Raven.captureBreadcrumb({
    message: 'request',
    category: 'request',
    data: { url, requestMethod },
  });

  const baseURL = Pollfish.helper.getBaseRequestURL();
  const promise = new Promise((resolve, reject) => {
    const requestURL = baseURL + url;
    const additionalRequestionOptions = {};
    if (dataType) {
      additionalRequestionOptions.dataType = dataType;
    }
    $.ajax({
      url: requestURL,
      type: requestMethod,
      contentType,
      tryCount: 0,
      retryLimit: 3,
      // all requests expect query params for the data
      data: requestData,
      ...additionalRequestionOptions,
      // we need the ajax this context here
      error: function Error(jqXHR, textStatus, errorThrown) {
        const statusCode = jqXHR.status;
        const { responseText } = jqXHR;
        if (
          statusCode === 504 ||
          statusCode === 503 ||
          textStatus === 'timeout'
        ) {
          this.tryCount += 1;
          if (this.tryCount <= this.retryLimit) {
            $.ajax(this);
            return;
          }
        }

        // statusCode zeroes are suppressed because they give no proper info and
        // continue to fill up our sentry account
        if (statusCode === 0 || statusCode === '0') {
          Pollfish.analytics.sendEvent('error', 'statusCodeZero', requestURL);
          reject();
          return;
        }

        if (suppressedStatuses.indexOf(statusCode) > -1) {
          Pollfish.analytics.sendEvent('http-error', statusCode, requestURL);
          reject();
          return;
        }
        Pollfish.analytics.sendEvent('error', 'request', requestURL);
        Raven.captureException(`[HTTP-ERROR]:${statusCode}`, {
          fingerprint: [statusCode],
          tags: {
            requestURL,
          },
          extra: {
            textStatus,
            statusCode,
            responseText,
            url: requestURL,
            errorThrown,
            data: requestData,
            interface: 'request',
          },
        });

        // since we are not passing an error object here, using the .catch for
        // .requests is not required. But we do handle the .catch with an empty
        // function for cases to suppress warnings
        reject();
      },
      success: resolve,
    });
  });

  return promise;
};

export default request;
